import { useEffect, useMemo, useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Checkbox } from '../../components/atoms/Checkbox'
import { CheckMark } from '../../components/atoms/CheckMark'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import {
  useProductVariantPricingHistory,
  type ProductVariantPricingHistoryRowDto
} from '../../../viewmodels/useProductVariantPricingHistory'
import {
  useCurrentYearVariantPricingHistory,
  type VariantAxis
} from '../../../viewmodels/useCurrentYearVariantPricingHistory'
import { CurrentYearVariantPricingHistoryCharts } from './CurrentYearVariantPricingHistoryCharts'

/**
 * /products/variant-pricing-history — READ-ONLY per-variant pricing ledger.
 *
 * 2026-05-27: setting the selling price moved to the Variants screen.
 * This screen now only displays the price-over-time history + charts.
 *
 * Layout:
 *   - Header + product picker dropdown
 *   - Current-year charts + a "Current only" filter
 *   - Eight columns: Axes, Cur (✓ on is_current), Baseline cost,
 *     Selling price, Profit, Margin, Status, Date — all read-only.
 *
 * Status badge: red "⚠ Bleeding" when row.is_bleeding === true, green
 * "● Healthy" otherwise.
 */
export function ProductVariantPricingHistoryPage(): JSX.Element {
  const {
    products,
    selectedProductId,
    history,
    total,
    page,
    pageSize,
    isLoading,
    error,
    currentOnly,
    setCurrentOnly,
    selectProduct,
    setPage,
    reload
  } = useProductVariantPricingHistory()

  // Per-axis chart filter: { custom_data_type_id → selected value_id }.
  // '' / absent = "All" for that axis. Charts-only — the table below is
  // unaffected. Reset when the product changes (old axes don't apply).
  const [axisFilter, setAxisFilter] = useState<Record<string, string>>({})
  useEffect(() => {
    setAxisFilter({})
  }, [selectedProductId])
  const selectedValueIds = useMemo(
    () => Object.values(axisFilter).filter((v) => v !== ''),
    [axisFilter]
  )

  // Chart fetched independently from /product-variant-pricing-history/current-year
  // so paginating the table below doesn't refetch the aggregates.
  const chart = useCurrentYearVariantPricingHistory(selectedProductId, selectedValueIds)

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-h2 text-charcoal">Variant Pricing History</h1>
        <ProductPicker
          products={products}
          selectedId={selectedProductId}
          onChange={selectProduct}
        />
      </header>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {selectedProductId !== null && chart.axes.length > 0 ? (
        <AxisFilterBar
          axes={chart.axes}
          selected={axisFilter}
          onChange={(typeId, valueId) =>
            setAxisFilter((prev) => ({ ...prev, [typeId]: valueId }))
          }
        />
      ) : null}

      {selectedProductId !== null ? (
        <CurrentYearVariantPricingHistoryCharts
          year={chart.year}
          monthly={chart.monthly}
          quarterly={chart.quarterly}
          isLoading={chart.isLoading}
          error={chart.error}
          onRetry={chart.reload}
        />
      ) : null}

      {selectedProductId !== null ? (
        <Checkbox
          label="Current only"
          checked={currentOnly}
          onChange={(e) => setCurrentOnly(e.target.checked)}
        />
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : selectedProductId === null ? (
        <Placeholder>Pick a product above to view its variant pricing.</Placeholder>
      ) : history.length === 0 ? (
        <Placeholder>
          No pricing snapshots for this product yet. Set a variant&apos;s selling
          price on the Variants screen to start its history.
        </Placeholder>
      ) : (
        <>
          <PricingTable history={history} />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}
    </div>
  )
}

function ProductPicker({
  products,
  selectedId,
  onChange
}: {
  readonly products: ReadonlyArray<{ id: string; name: string; category_name: string }>
  readonly selectedId: string | null
  readonly onChange: (next: string | null) => void
}): JSX.Element {
  return (
    <div className="w-full sm:w-auto sm:min-w-[18rem] sm:max-w-md">
      <Select
        value={selectedId ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        aria-label="Product"
      >
        <option value="">— Select a product —</option>
        {products.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} ({p.category_name})
          </option>
        ))}
      </Select>
    </div>
  )
}

function AxisFilterBar({
  axes,
  selected,
  onChange
}: {
  readonly axes: ReadonlyArray<VariantAxis>
  readonly selected: Record<string, string>
  readonly onChange: (typeId: string, valueId: string) => void
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {axes.map((axis) => (
        <div key={axis.type_id} className="w-full sm:w-auto sm:min-w-[10rem]">
          <label className="block font-body text-body-xs text-text-secondary mb-1">
            {axis.type_name}
          </label>
          <Select
            value={selected[axis.type_id] ?? ''}
            onChange={(e) => onChange(axis.type_id, e.target.value)}
            aria-label={`Filter charts by ${axis.type_name}`}
          >
            <option value="">All {axis.type_name}</option>
            {axis.values.map((v) => (
              <option key={v.value_id} value={v.value_id}>
                {v.value}
              </option>
            ))}
          </Select>
        </div>
      ))}
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}

function PricingTable({
  history
}: {
  readonly history: ReadonlyArray<ProductVariantPricingHistoryRowDto>
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Axes</Th>
            <Th className="text-center">Cur</Th>
            <Th>Baseline cost</Th>
            <Th>Selling price</Th>
            <Th>Profit</Th>
            <Th>Margin</Th>
            <Th>Status</Th>
            <Th>Date</Th>
          </tr>
        </thead>
        <tbody>
          {history.map((row) => (
            <PricingRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

function PricingRow({ row }: { readonly row: ProductVariantPricingHistoryRowDto }): JSX.Element {
  return (
    <tr className={`border-t border-border ${row.is_current ? 'bg-bamboo/10' : 'opacity-60'}`}>
      <Td>
        {row.axes_label !== null ? (
          <span className="font-mono text-text-secondary">{row.axes_label}</span>
        ) : (
          <span className="text-text-secondary">—</span>
        )}
      </Td>
      <Td className="text-center">
        <CheckMark on={row.is_current} label="Current" />
      </Td>
      <Td className="font-mono">
        {row.baseline_cost_paisa !== null
          ? formatRupeesDisplay(row.baseline_cost_paisa)
          : <span className="text-text-secondary">—</span>}
      </Td>
      <Td className="font-mono">{formatRupeesDisplay(row.selling_price_paisa)}</Td>
      <Td className="font-mono">
        {row.profit_amount_paisa !== null
          ? formatRupeesDisplay(row.profit_amount_paisa)
          : <span className="text-text-secondary">—</span>}
      </Td>
      <Td className="font-mono">{formatMarginBps(row.margin_bps)}</Td>
      <Td>
        {row.is_bleeding ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-terra/15 text-terra font-medium text-body-xs"
            title={`margin ${formatMarginBps(row.margin_bps)} < target ${formatMarginBps(row.target_margin_bps)} − tolerance ${formatMarginBps(row.margin_tolerance_bps)}`}
          >
            ⚠ Bleeding
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-forest/15 text-forest font-medium text-body-xs">
            ● Healthy
          </span>
        )}
      </Td>
      <Td className="text-text-secondary whitespace-nowrap">{formatDate(row.created_at)}</Td>
    </tr>
  )
}

/* ───────────────── formatting helpers ───────────────── */

function formatRupeesDisplay(paisa: number): string {
  const sign = paisa < 0 ? '-' : ''
  return `${sign}₹${Math.abs(paisa / 100).toFixed(2)}`
}

function formatMarginBps(bps: number | null): string {
  if (bps === null) return '—'
  return `${(bps / 100).toFixed(2)}%`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
